const CAP30 = {
  "id": 53,
  "title": "El Halving de Bitcoin",
  "slug": "el-halving-de-bitcoin",
  "type": "lesson",
  "category": "bitcoin",
  "level": "basic",
  "sections": [
    {
      "heading": "¿Qué es el halving?",
      "text": [
        "El halving es un mecanismo programado en el protocolo de Bitcoin para que la recompensa que reciben los mineros se reduzca a la mitad cada cierto número de bloques.",
        "Su objetivo principal es controlar la emisión de nuevos bitcoin y evitar que todas las monedas se repartan demasiado rápido o se concentren en pocas manos.",
        "Cada vez que ocurre un halving, la cantidad de BTC que se entrega como recompensa por cada bloque minado se divide entre dos.",
        "Satoshi Nakamoto comparó este proceso con la minería de oro: los mineros gastan recursos para agregar oro a la circulación; en Bitcoin, se gasta tiempo de CPU y electricidad para añadir nuevas monedas a la red."
      ]
    },
    {
      "heading": "Control de la emisión y distribución",
      "text": [
        "Gracias al halving, la emisión de Bitcoin está perfectamente planificada y es predecible.",
        "Al inicio se entregaban 50 BTC por bloque; con cada halving esa cifra se ha ido reduciendo, pero nunca se ha detenido la red.",
        "Este proceso ha permitido que la moneda se vaya distribuyendo de forma escalonada en el tiempo, facilitando una 'democratización' inicial del reparto y evitando que todos los BTC se minen de golpe."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/bitcoin-halving-supply.webp",
        "alt": "Curva de emisión de Bitcoin mostrando cómo se reduce la recompensa por bloque con cada halving.",
        "caption": "Curva de emisión de Bitcoin: cada halving reduce la recompensa y hace que la emisión sea cada vez más lenta."
      }
    },
    {
      "heading": "Evolución de la emisión de Bitcoin",
      "text": [
        "Si miramos la evolución de Bitcoin hasta hoy, ya se ha emitido más del 86% de todos los BTC que existirán.",
        "Con los primeros 210.000 bloques se alcanzó el 50% de los bitcoin en circulación.",
        "Los cálculos de todos los halvings están hechos desde el inicio: se sabe cuántos BTC se entregan en cada etapa y en qué bloque terminará cada una."
      ],
      "list": [
        "Suministro máximo: 21 millones de BTC.",
        "Número de bloques por halving: 210.000.",
        "Etapas de halving previstas: 34 aproximadamente.",
        "Bloque final estimado: 6.930.000, alrededor del año 2140."
      ]
    },
    {
      "heading": "Recompensa por bloque a lo largo del tiempo",
      "text": [
        "Cada halving reduce la recompensa a la mitad. Al principio eran 50 BTC por bloque.",
        "Después del primer halving: 25 BTC por bloque.",
        "Después del segundo: 12,5 BTC por bloque.",
        "Actualmente (tras varios halvings) la recompensa es de 6,25 BTC por bloque, y seguirá bajando.",
        "En el sexto halving, la recompensa por bloque será de menos de 0,8 BTC, y así sucesivamente hasta que finalmente llegue a cero."
      ],
      "image": {
        "src": "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/tabla-todos-los-halving-bitcoin.webp",
        "alt": "Tabla con las recompensas por bloque en distintos halvings de Bitcoin.",
        "caption": "Ejemplo de cómo la recompensa por bloque se reduce a la mitad en cada halving programado."
      }
    },
    {
      "heading": "¿Qué pasa cuando la recompensa llega a cero?",
      "text": [
        "En el último halving, la recompensa en BTC por bloque será prácticamente cero.",
        "A partir de ese momento, los mineros sólo cobrarán las comisiones de las transacciones incluidas en cada bloque.",
        "Este punto se estima que llegará alrededor del año 2140, por lo que aún falta mucho tiempo."
      ]
    },
    {
      "heading": "¿Qué supone el halving para la red Bitcoin?",
      "text": [
        "El halving no detiene la red ni requiere un reinicio especial. Está programado en el código y se ejecuta de forma automática cuando se alcanza el bloque correspondiente.",
        "Lo único que cambia es que, a partir de ese bloque, se emite la mitad de BTC por cada bloque minado."
      ]
    },
    {
      "heading": "Consecuencias para los usuarios",
      "text": [
        "Para la persona que usa Bitcoin en su vida diaria para enviar y recibir pagos, el halving casi no se nota.",
        "No se cambia el funcionamiento de las carteras, ni la forma de hacer transacciones, ni la experiencia de uso.",
        "Lo que sí puede cambiar con el tiempo es el comportamiento del precio o las comisiones, pero el proceso técnico del envío sigue igual."
      ]
    },
    {
      "heading": "Consecuencias para los mineros",
      "text": [
        "Los mineros son quienes más sienten el impacto del halving.",
        "Al reducirse a la mitad la recompensa, algunos mineros con equipos poco eficientes pueden dejar de minar si sus costos superan a sus ingresos, especialmente si el precio del BTC no sube.",
        "Esto empuja a una competencia por ser más eficientes energéticamente y optimizar el hardware y la infraestructura.",
        "A largo plazo, el halving incentiva la profesionalización y mejora tecnológica del sector minero."
      ]
    },
    {
      "heading": "Consecuencias para los especuladores e inversores",
      "text": [
        "Para los especuladores, el halving suele verse como un momento clave.",
        "Al reducirse la emisión de nuevos BTC, la oferta nueva que llega al mercado baja a la mitad.",
        "Si la demanda se mantiene igual o aumenta, la lógica económica sugiere que el precio puede tender a subir, aunque nada garantiza un resultado concreto.",
        "Por eso se dice que en los periodos de halving 'se pesca en río revuelto': hay volatilidad y quienes saben manejar el riesgo pueden obtener beneficios, pero también pueden sufrir pérdidas importantes."
      ]
    },
    {
      "heading": "¿Cada cuánto ocurre un halving?",
      "text": [
        "El halving ocurre aproximadamente cada 4 años, pero en realidad está definido por bloques, no por fechas.",
        "Se produce cada 210.000 bloques minados."
      ],
      "list": [
        "Tiempo medio por bloque: ~10 minutos (ajustado por dificultad cada 2.016 bloques).",
        "Cálculo aproximado: 210.000 bloques × 10 minutos = 2.100.000 minutos.",
        "2.100.000 minutos ÷ 60 = 35.000 horas.",
        "35.000 horas ≈ 1.458 días, es decir, unos 4 años."
      ]
    },
    {
      "heading": "¿Qué hubiera pasado sin halving?",
      "text": [
        "Si Bitcoin no tuviera halving, todas las monedas se habrían minado en pocos años.",
        "Inicialmente se entregaban 50 BTC por bloque, lo que representa:",
        "• 300 BTC por hora (50 × 6 bloques/hora).\n• 7.200 BTC por día.\n• 2.628.000 BTC al año.",
        "Si dividimos los 21 millones de BTC entre 2.628.000 BTC/año, obtendríamos que en menos de 8 años se habría emitido todo el suministro.",
        "En ese escenario, los mineros dependerían únicamente de comisiones muy pronto, y probablemente Bitcoin no habría tenido tiempo de crecer lo suficiente como para que la minería fuera rentable.",
        "El halving, por tanto, es clave para la sostenibilidad del sistema, ya que extiende la emisión durante más de un siglo y refuerza la narrativa de escasez digital."
      ]
    },
    {
      "heading": "¿Cómo participar sin comprar 1 BTC completo?",
      "text": [
        "Nadie puede asegurarte un precio futuro específico para Bitcoin, pero sí es cierto que el suministro es limitado a 21 millones.",
        "No es necesario comprar 1 BTC entero para participar. Bitcoin es divisible hasta en 100 millones de unidades llamadas satoshis.",
        "Puedes comprar la fracción que quieras en tu moneda local. Si con el tiempo el precio de Bitcoin aumenta, el valor de tu fracción se moverá en la misma proporción."
      ]
    },
    {
      "heading": "Resumen",
      "list": [
        "El halving reduce a la mitad la recompensa de los mineros cada 210.000 bloques.",
        "Controla la emisión de nuevos BTC y refuerza la escasez digital.",
        "No afecta el uso diario de Bitcoin, pero sí impacta a mineros e inversores.",
        "Ocurre aproximadamente cada 4 años y se repetirá hasta alrededor del año 2140.",
        "Sin halving, todos los BTC se habrían emitido en menos de una década y el modelo económico sería muy distinto."
      ]
    }
  ]
};

export default CAP30;
